<template>
  <div
    class="copy-info-container tw-relative tw-flex tw-flex-row tw-items-center tw-justify-between"
  >
    <p
      v-if="props.isShowText"
      class="text-copy-container tw-mb-0 tw-pr-[20px] tw-truncate"
    >
      <span data-cy="copy-info-text" :class="props.textClass">{{
        props.text
      }}</span>
    </p>
    <div
      class="icon-container tw-flex tw-p-[6px] tw-cursor-pointer"
      @click="handleCopyText"
    >
      <svg
        :class="props.styleIconCss"
        xmlns="http://www.w3.org/2000/svg"
        :view-box="`0 0 ${props.iconHeight} ${props.iconHeight}`"
        fill="none"
      >
        <path
          d="M16.455 2.7075L14.1525 0.4125C13.8847 0.14838 13.5237 0.000213088 13.1475 1.36879e-10L6.675 1.36879e-10C6.29836 -5.21649e-06 5.93704 0.149098 5.67002 0.414718C5.40299 0.680337 5.25198 1.04087 5.25 1.4175V3.75H2.55C2.17336 3.74999 1.81204 3.8991 1.54502 4.16472C1.27799 4.43034 1.12698 4.79087 1.125 5.1675V16.5825C1.12698 16.9591 1.27799 17.3197 1.54502 17.5853C1.81204 17.8509 2.17336 18 2.55 18H11.325C11.7016 18 12.063 17.8509 12.33 17.5853C12.597 17.3197 12.748 16.9591 12.75 16.5825V14.25H15.45C15.8266 14.25 16.188 14.1009 16.455 13.8353C16.722 13.5697 16.873 13.2091 16.875 12.8325V3.75C16.8802 3.55672 16.8456 3.36444 16.7734 3.18509C16.7011 3.00575 16.5927 2.84319 16.455 2.7075ZM10.875 16.5H3C2.90054 16.5 2.80516 16.4605 2.73484 16.3902C2.66451 16.3198 2.625 16.2245 2.625 16.125V5.625C2.625 5.52554 2.66451 5.43016 2.73484 5.35984C2.80516 5.28951 2.90054 5.25 3 5.25H8.835C8.88358 5.24888 8.93188 5.25761 8.97699 5.27565C9.0221 5.2937 9.0631 5.32069 9.0975 5.355L11.1375 7.35C11.1737 7.38491 11.2024 7.42688 11.2218 7.47333C11.2411 7.51977 11.2507 7.56969 11.25 7.62V16.125C11.25 16.2245 11.2105 16.3198 11.1402 16.3902C11.0698 16.4605 10.9745 16.5 10.875 16.5ZM15 12.75H12.9375C12.8878 12.75 12.8401 12.7302 12.8049 12.6951C12.7698 12.6599 12.75 12.6122 12.75 12.5625V7.5C12.7501 7.313 12.7131 7.12784 12.641 6.95531C12.5689 6.78277 12.4632 6.6263 12.33 6.495L10.0275 4.1625C9.7532 3.89427 9.38363 3.7459 9 3.75H6.9375C6.88777 3.75 6.84008 3.73025 6.80492 3.69508C6.76975 3.65992 6.75 3.61223 6.75 3.5625V1.875C6.75 1.77554 6.78951 1.68016 6.85983 1.60984C6.93016 1.53951 7.02554 1.5 7.125 1.5H12.9375C12.9878 1.49927 13.0377 1.50888 13.0842 1.52823C13.1306 1.54758 13.1726 1.57626 13.2075 1.6125L15.2625 3.6C15.2987 3.63491 15.3274 3.67688 15.3468 3.72333C15.3661 3.76977 15.3757 3.81969 15.375 3.87V12.375C15.375 12.4745 15.3355 12.5698 15.2652 12.6402C15.1948 12.7105 15.0995 12.75 15 12.75Z"
          :fill="props.iconColor"
        />
      </svg>
    </div>

    <CustomAlert
      ref="alertRef"
      :class="`${props.alertClass} tw-absolute tw-right-[0%] tw-translate-x-[35px] tw-translate-y-[35px] tw-bg-[#FFFFFF]`"
    >
      <div
        class="tw-leading-none tw-text-[#211F58] tw-text-[13px] tw-font-medium tw-px-[12px] tw-py-[12px]"
      >
        {{ getUiText("app-settings-domain-manage-modal-copy-popover-1") }}
      </div>
    </CustomAlert>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import CustomAlert from "./customAlert.vue";
import store from "@/store";
import utilities from "@/utils/utilities";
import commonServices from "@/services/common/commonServices";

const commonServicesInstance = new commonServices();
const alertRef = ref<InstanceType<typeof CustomAlert> | null>(null);
const emit = defineEmits(["click:copied"]);

const props = defineProps({
  text: {
    type: String,
    required: true,
  },
  styleIconCss: {
    type: String,
    default: "",
  },
  iconColor: {
    type: String,
    default: "#387DFF",
  },
  iconHeight: {
    type: String,
    default: "18",
  },
  isShowText: {
    type: Boolean,
    default: true,
  },
  textClass: {
    type: String,
    default: "",
    required: false,
  },
  alertClass: {
    type: String,
    default: "",
    required: false,
  },
});

const defaultUiText = computed(() => store.state.LanguagePicker.defaultUiText);
const languageSelectedText = computed(
  () => store.state.LanguagePicker.languageSelectedText,
);
const getUiText = (id: string, isArray = false) => {
  return utilities.getUiText(
    id,
    languageSelectedText.value,
    defaultUiText.value,
    isArray,
  );
};

const handleCopyText = () => {
  commonServicesInstance.copyTextToClipboard(props.text);
  emit("click:copied");
  handleShowCopiedAlert();
};

const handleShowCopiedAlert = () => alertRef.value?.handleShow();
</script>
<style lang="scss" scoped>
.icon-container {
  border-radius: 6px;

  &:hover {
    background-color: #f7f7f8;
  }

  &:active {
    opacity: 0.75;
  }
}
</style>
